import { Fragment, useMemo, useRef } from 'react';
import Tooltip from '@material-ui/core/Tooltip';

import CustomTooltip from '@/components/CustomTooltip';
import HorizontalScroll from '@/components/HorizontalScroll';
import { toastError, toastSuccess } from '@/components/Toast';
import { useGraphicsAndThreeDRemarksMutation } from '@/features/my-dashborad/api/myDashboardApi';
import {
	dashboardGroupBy,
	handleTextareaHeight,
	formatDashboardDate,
	renderLeadTimeVariance
} from '@/features/my-dashborad/utils';
import { getShortName, renderSetImages } from '@/utils/Util';
import SetStyleSubCategory from '@/components/SetStyleSubCategory';
const ThreeDListView = ({
	listViewResponse,
	onClickDesignImage,
	handleSelectSampleItem,
	handleSelectAllSampleItem,
	selectedSampleItem = [],
	selectedGroupBy = null
}) => {
	const [graphicsAndThreeDRemarks, { isLoading: isUpdatingGraphicsAndThreeDRemarks }] =
		useGraphicsAndThreeDRemarksMutation();
	const elementRef = useRef(null);

	const THREE_D_TABLE_COL_SPAN = 14;
	const rows = Array.isArray(listViewResponse) ? listViewResponse : [];
	const groupedRows = useMemo(() => {
		if (!selectedGroupBy?.id || !rows.length) return null;
		return dashboardGroupBy(rows, selectedGroupBy.id);
	}, [rows, selectedGroupBy?.id]);
	const displayGroupEntries = useMemo(() => {
		if (!groupedRows) return null;
		return Object.entries(groupedRows);
	}, [groupedRows]);

	const handleGetRemarks = async (item, remarks) => {
		try {
			await graphicsAndThreeDRemarks({ id: item?.threeDJourneyDetails?.requestId, remarks }).unwrap();
			toastSuccess('Remarks updated successfully');
		} catch ({ error }) {
			toastError(error?.data?.message || 'Something went wrong');
		}
	};
	const selectedIds = rows?.map((sample) => parseInt(sample?.id)).filter((id) => !Number.isNaN(id));
	const isAllSelected =
		selectedIds?.length > 0 && selectedIds.every((id) => selectedSampleItem.includes(id));

	const renderGeneralListView = (item, idx) => {
		const sampleItemId = parseInt(item?.id);
		const isChecked = !Number.isNaN(sampleItemId) && selectedSampleItem.includes(sampleItemId);
		return (
			<tr key={`${item.id}_${idx}`}>
				<td
					rowSpan={item?.sampleInventoryItemResponse?.noOfSameProduct || 1}
					className={`select-box-column general-check-box-column text-center align-middle ${item?.sampleInventoryItemResponse?.isSameProduct ? 'd-none' : ''}`}
				>
					<div className="custom-chekbox">
						<div className="form-group m-0">
							<input
								type="checkbox"
								id={`general_list_${item?.id}`}
								name={`general_list_${item?.id}`}
								value={sampleItemId}
								checked={isChecked}
								onChange={handleSelectSampleItem}
							/>
							<label htmlFor={`general_list_${item?.id}`} />
						</div>
					</div>
				</td>
				<td
					rowSpan={item?.sampleInventoryItemResponse?.noOfSameProduct || 1}
					className={`general-image-column checkbox-column cursor-pointer ${item?.sampleInventoryItemResponse?.isSameProduct ? 'd-none' : ''}`}
					onClick={() => onClickDesignImage(item?.id)}
				>
					<div className="d-flex align-items-center">
						{item?.isSet ? (
							renderSetImages(item.designDocuments?.[0]?.docUrl, '56px')
						) : (
							<img
								src={item?.designDocuments?.[0]?.docUrl}
								alt=""
								className="cell-img pr-2 cursor-pointer"
							/>
						)}

						<div className="d-inline-block align-middle ml-1 cursor-pointer">
							{item?.isSet && <span className="status set">Set</span>}
							{item?.isPack && <span className="status pack">Pack</span>}
							<p className="regular-14 weight-500 gray_dark_01 my-1">{item?.referenceNumber}</p>
							<ul className="common-list-component mb-1">
								{!item?.isSet && item?.productGroup && (
									<li className="regular-14">{item?.productGroup}</li>
								)}
								{item?.isSet && item?.setMemberProducts?.[0]?.productGroup && (
									<li className="regular-14">
										{item?.setMemberProducts?.[0]?.productGroup}
									</li>
								)}
								{!item?.isSet && item?.subCategory && (
									<li className="regular-14">
										<Tooltip title={item?.subCategory || ''} placement="top" arrow>
											<span>{getShortName(item?.subCategory, 25)}</span>
										</Tooltip>
									</li>
								)}
								{item?.isSet && <SetStyleSubCategory product={item?.productResponse} />}
								{item?.addedByName && (
									<li className="regular-14">
										<Tooltip
											title={
												<span className="regular-14">{item?.addedByName || ''}</span>
											}
											placement="top"
											arrow
										>
											<span>{getShortName(item?.addedByName, 10)}</span>
										</Tooltip>
									</li>
								)}
							</ul>
						</div>
					</div>
				</td>
				<td
					className={`general-last-status-column ${item?.sampleInventoryItemResponse?.isSameProduct ? 'd-none' : ''}`}
					rowSpan={item?.sampleInventoryItemResponse?.noOfSameProduct || 1}
				>
					{item?.lastTaskCreatedAt && (
						<>
							<span className="status yellow-1 status-with-border d-inline-block mb-1">
								{item?.lastTask.name}
							</span>

							<p className="regular-14 gray_dark_02 mb-0">
								{formatDashboardDate(item?.lastTaskCreatedAt)},{' '}
								{item?.lastTaskCreatedBy.nickName}
							</p>
						</>
					)}
				</td>
				<td
					rowSpan={item?.sampleInventoryItemResponse?.noOfSameProduct || 1}
					className={`general-collection-column ${item?.sampleInventoryItemResponse?.isSameProduct ? 'd-none' : ''}`}
				>
					<p
						className="regular-14 gray_dark_02 mb-0 cursor-pointer text-underline"
						onClick={() => window.open(`/collections/view/${item?.collectionId}`, '_blank')}
					>
						{item?.collectionName}
					</p>
				</td>
				<td
					rowSpan={item?.sampleInventoryItemResponse?.noOfSameProduct || 1}
					className={`print-and-embo-column ${item?.sampleInventoryItemResponse?.isSameProduct ? 'd-none' : ''}`}
				>
					<p className="regular-14 gray_dark_02 mb-0">
						Print:{' '}
						<span className="gray_dark_01">
							{item?.printCategoryList?.map((item) => item?.name).join(', ')}
						</span>
					</p>
				</td>
				<td
					rowSpan={item?.sampleInventoryItemResponse?.noOfSameProduct || 1}
					className={`print-and-embo-column ${item?.sampleInventoryItemResponse?.isSameProduct ? 'd-none' : ''}`}
				>
					<p className="regular-14 gray_dark_02 mb-0">
						Embo:{' '}
						<span className="gray_dark_01">
							{item?.embroideryCategoryList?.map((item) => item?.name).join(', ')}
						</span>
					</p>
				</td>
				<td
					rowSpan={item?.sampleInventoryItemResponse?.noOfSameProduct || 1}
					className={`wash-column ${item?.sampleInventoryItemResponse?.isSameProduct ? 'd-none' : ''}`}
				>
					<p className="regular-14 gray_dark_02 mb-0">
						{item?.washCategoryList?.map((item) => item?.name).join(', ')}
					</p>
				</td>
				<td
					rowSpan={item?.sampleInventoryItemResponse?.noOfSameProduct || 1}
					className={`fabric-column ${item?.sampleInventoryItemResponse?.isSameProduct ? 'd-none' : ''}`}
				>
					<p className="regular-14 gray_dark_02 mb-0">
						{item?.mainFabricType.toLowerCase()?.charAt(0).toUpperCase() +
							item?.mainFabricType.toLowerCase()?.slice(1)}
						, {item?.fabricName}
					</p>
				</td>
				<td
					rowSpan={item?.sampleInventoryItemResponse?.noOfSameProduct || 1}
					className={`trims fabric-column ${item?.sampleInventoryItemResponse?.isSameProduct ? 'd-none' : ''}`}
				>
					<ul className="with-bullet">
						{item?.trimsResponses?.map((trim) => (
							<li className="regular-12 gray_dark_02" key={trim?.id}>
								<CustomTooltip
									placement="top"
									arrowCenter={true}
									text={trim?.name}
									className={trim?.name?.length > 120 ? 'show' : 'hide'}
								>
									{getShortName(trim?.name, 120)}
								</CustomTooltip>
							</li>
						))}
					</ul>
				</td>
				<td
					rowSpan={item?.sampleInventoryItemResponse?.noOfSameProduct || 1}
					className={`tag tag-columm ${item?.sampleInventoryItemResponse?.isSameProduct ? 'd-none' : ''}`}
				>
					<div class="design-tags d-flex flex-column align-items-start">
						{item?.tagResponseList?.map((tagItem) => (
							<span
								className="status radius"
								style={{ background: tagItem?.colorCode }}
								key={tagItem?.id}
							>
								{tagItem?.text}
							</span>
						))}
					</div>
				</td>
				<td className="3d-column date-column">
					{item?.threeDJourneyDetails?.requestDate && (
						<p className="regular-14 gray_dark_02 mb-0 date-column">
							Request:{' '}
							<span className="gray_dark_01">
								{formatDashboardDate(item?.threeDJourneyDetails?.requestDate)}
							</span>
						</p>
					)}
					{item?.threeDJourneyDetails?.completeDate && (
						<p className="regular-14 gray_dark_02 mb-0 date-column">
							Complete:{' '}
							<span className="gray_dark_01">
								{formatDashboardDate(item?.threeDJourneyDetails?.completeDate)}
							</span>
						</p>
					)}
					{item?.threeDJourneyDetails?.approvedDate && (
						<p className="regular-14 gray_dark_02 mb-0 date-column">
							Approved:{' '}
							<span className="gray_dark_01">
								{formatDashboardDate(item?.threeDJourneyDetails?.approvedDate)}
							</span>
						</p>
					)}
				</td>
				<td>
					{item?.threeDJourneyDetails?.actualLeadTimeDays && (
						<p className="regular-14 gray_dark_02 mb-0">
							{item?.threeDJourneyDetails?.actualLeadTimeDays} days
						</p>
					)}
				</td>
				<td>
					{renderLeadTimeVariance(
						item?.threeDJourneyDetails?.requiredLeadTimeDays,
						item?.threeDJourneyDetails?.actualLeadTimeDays
					)}
				</td>
				<td
					className={`remark-column text-field-column ${item?.sampleInventoryItemResponse?.isSameProduct ? 'd-none' : ''}`}
					rowSpan={item?.sampleInventoryItemResponse?.noOfSameProduct || 1}
				>
					<textarea
						type="text"
						defaultValue={item?.threeDJourneyDetails?.remarks ?? ''}
						onBlur={(e) => handleGetRemarks(item, e.target.value)}
						onChange={handleTextareaHeight}
						onKeyDown={(e) => {
							if (e.key === 'Enter') e.currentTarget.blur();
						}}
						style={{ resize: 'none', overflow: 'hidden' }}
						disabled={isUpdatingGraphicsAndThreeDRemarks}
					/>
				</td>
				<td className="sample-td location-td">
					<p className="regular-14 gray_dark_02 mb-0">
						{item?.sampleInventoryItemResponse?.countryResponse?.code}
						{item?.sampleInventoryItemResponse?.countryResponse?.code && ': '}
						{item?.sampleInventoryItemResponse?.sampleId}
					</p>
				</td>
				<td className="location-td">
					<p className="regular-14 gray_dark_02 mb-0">
						{[
							item?.sampleInventoryItemResponse?.sampleInventoryLocationResponse?.locationId,
							item?.sampleInventoryItemResponse?.sampleInventoryLocationResponse
								?.buildingTagResponse?.text,
							item?.sampleInventoryItemResponse?.sampleInventoryLocationResponse
								?.floorTagResponse?.text,
							item?.sampleInventoryItemResponse?.sampleInventoryLocationResponse
								?.roomOrAreaTagResponse?.text,
							item?.sampleInventoryItemResponse?.sampleInventoryLocationResponse
								?.colorTagResponse?.text,
							item?.sampleInventoryItemResponse?.sampleInventoryLocationResponse?.sl
						]
							.filter((value) => value !== null && value !== undefined && value !== '')
							.join(', ')}
					</p>
				</td>
			</tr>
		);
	};

	return (
		<div>
			<div className="list-view-table-container mt-4 mb-4">
				<HorizontalScroll elementReference={elementRef.current} />
				<div className="header-sticky-table general-list-table three-d-list-table">
					<table
						className={`table with-border scroll-x-label scroll-y-label ${rows?.length > 0 ? '' : 'd-table'}`}
						ref={elementRef}
					>
						<thead>
							<tr>
								<th className="select-box-column checkbox-column general-check-box-column text-center align-middle">
									<div className="custom-chekbox">
										<div className="form-group m-0">
											<input
												type="checkbox"
												id="selectAllGeneralList"
												name="selectAllGeneralList"
												onChange={handleSelectAllSampleItem}
												checked={isAllSelected}
											/>
											<label htmlFor="selectAllGeneralList" />
										</div>
									</div>
								</th>
								<th className="general-image-column">Design</th>
								<th className="general-last-status-column">Last status</th>
								<th className="general-collection-column">Collection</th>
								<th>Print</th>
								<th>Embroidery</th>
								<th>Wash</th>
								<th>Fabric</th>
								<th>Trims</th>
								<th>Tag</th>
								<th>3D</th>
								<th>
									<span className="white-space-nowrap">Actual lead time:</span>
									<br />
									<span className="white-space-nowrap">3D request → 3D completed</span>
								</th>
								<th>
									<span className="white-space-nowrap">Required lead time:</span>
									<br />
									<span className="white-space-nowrap">3D request → Required date</span>
								</th>
								<th>Remarks</th>
								<th className="white-space-nowrap">Sample ID</th>
								<th>Location</th>
							</tr>
						</thead>
						<tbody>
							{displayGroupEntries?.length ? (
								displayGroupEntries.map(([groupName, items]) => (
									<Fragment key={`group_${groupName}`}>
										<tr className="sample-journey-group-header-row">
											<td
												colSpan={THREE_D_TABLE_COL_SPAN}
												className="bg-white group-title-header border-bottom-0 pt-3 pb-2"
											>
												<div className="group-title-header-content">
													<span className="semibold-14 mr-2 gray_dark_01">
														{selectedGroupBy?.name === 'Request-wise' ? (
															<span className="text-underline">
																{getShortName(groupName, 50)}
															</span>
														) : (
															<span className="gray_dark_01">
																{getShortName(groupName, 50)}
															</span>
														)}
														<span className="regular-14 gray_dark_01">
															({items?.length ?? 0})
														</span>
													</span>
												</div>
											</td>
										</tr>
										{items?.map((item, idx) => renderGeneralListView(item, idx))}
									</Fragment>
								))
							) : rows?.length ? (
								rows.map((item, idx) => renderGeneralListView(item, idx))
							) : (
								<tr>
									<td className="text-center align-middle" colSpan={16}>
										Data not found
									</td>
								</tr>
							)}
						</tbody>
					</table>
				</div>
			</div>
		</div>
	);
};

export default ThreeDListView;
